import _ from "lodash";
import {
  PARTICIPANT_USER_TYPES,
  USER_TYPE_COUNT_FIELDS,
} from "./GradesConstants";

/**
 * Builds a school -> [gradeIds] hierarchy from Phase 1 query grades array.
 * Grades are ordered by displaySequence within each school.
 *
 * @param {Array} grades - Grade objects from getOrganizationGradeIdsHierarchyQuery
 * @returns {{ hierarchy: Object, allSchoolIds: string[], allGradeIds: string[] }}
 */
export const buildGradeHierarchyFromEdges = (grades = []) => {
  const hierarchy = {};
  const schoolIdSet = new Set();
  const allGradeIds = [];

  // Sort by displaySequence first so hierarchy insertion order is correct
  const sorted = _.sortBy(grades, "displaySequence");

  _.forEach(sorted, grade => {
    const gradeId = grade.id;
    const schoolId = _.get(grade, "curriculumProgram.id");

    if (!gradeId || !schoolId) return;

    schoolIdSet.add(schoolId);
    allGradeIds.push(gradeId);

    if (!hierarchy[schoolId]) {
      hierarchy[schoolId] = [];
    }
    hierarchy[schoolId].push(gradeId);
  });

  return {
    hierarchy,
    allSchoolIds: Array.from(schoolIdSet),
    allGradeIds,
  };
};

/**
 * Returns filtered hierarchy for a set of school IDs, or the full hierarchy
 * when selectedSchoolIds is null (All Schools mode).
 *
 * @param {Object} hierarchy - Full hierarchy map { schoolId -> [gradeIds] }
 * @param {null|Set<string>} selectedSchoolIds - null = all schools, Set = specific selection
 * @returns {Object} Filtered hierarchy
 */
export const getFilteredGradeHierarchy = (hierarchy, selectedSchoolIds) => {
  if (selectedSchoolIds === null) return hierarchy;
  if (selectedSchoolIds.size === 0) return {};
  return _.pickBy(hierarchy, (_gradeIds, schoolId) =>
    selectedSchoolIds.has(schoolId)
  );
};

/**
 * Returns grade IDs belonging only to the selected schools.
 * When selectedSchoolIds is null (All Schools), returns allGradeIds unchanged.
 *
 * @param {Object} hierarchy - Full hierarchy map { schoolId -> [gradeIds] }
 * @param {null|Set<string>} selectedSchoolIds - null = all schools, Set = specific selection
 * @param {string[]} allGradeIds - All grade IDs
 * @returns {string[]} Grade IDs for selected schools only
 */
export const getFilteredSchoolGradeIds = (
  hierarchy,
  selectedSchoolIds,
  allGradeIds
) => {
  if (selectedSchoolIds === null) return allGradeIds;
  if (selectedSchoolIds.size === 0) return [];

  const ids = [];
  selectedSchoolIds.forEach(schoolId => {
    const gradeIds = hierarchy[schoolId];
    if (!gradeIds) return;
    ids.push(...gradeIds);
  });
  return _.uniq(ids);
};

/**
 * Filters grade IDs by search text, matching against grade names in the details map.
 * Grades whose details haven't loaded yet are excluded from search results (not shown as matches).
 *
 * @param {string[]} gradeIds - Grade IDs to filter
 * @param {Object} gradeDetailsMap - Map of gradeId -> { name, ... }
 * @param {string} searchText - Search string
 * @returns {string[]} Filtered grade IDs
 */
export const filterGradesBySearch = (gradeIds, gradeDetailsMap, searchText) => {
  if (!searchText) return gradeIds;

  const lowerSearch = searchText.toLowerCase();

  return _.filter(gradeIds, id => {
    const name = _.get(gradeDetailsMap, `${id}.name`, "");
    return name.toLowerCase().includes(lowerSearch);
  });
};

/**
 * Formats participant count subtitle from grade count data.
 * e.g. "32 students, 4 staff members and 51 family members"
 *
 * @param {Object} countData - Object with staffCount, studentCount, familyMemberCount
 * @param {string[]} selectedUserTypes - User types to include
 * @param {Function} t - Translation function
 * @returns {string} Formatted subtitle
 */
export const computeGradeCountSubtitle = (countData, selectedUserTypes, t) => {
  if (_.isEmpty(countData)) return "";

  return _.map(selectedUserTypes, (userType, index) => {
    const countField = USER_TYPE_COUNT_FIELDS[userType];
    const count = _.get(countData, countField, 0);
    const label = t(PARTICIPANT_USER_TYPES[userType], { count });
    return `${index ? ", " : ""}${count} ${label}`;
  }).join("");
};
